export const photographersData = {
  industry: "Photographers",
  slug: "photographers",
  
  // SEO Meta Tags
  seo: {
    title: "Intake Forms for Photographers | HeyIntake",
    description: "Automate client inquiries, bookings, and onboarding for your photography business with AI-powered intake forms. Qualify clients, streamline bookings, and save hours every week.",
    keywords: [
      "photography intake form",
      "photographer booking form",
      "client questionnaire for photographers",
      "photography client onboarding",
      "wedding photography intake",
      "portrait photography booking",
      "event photography form"
    ]
  },

  heroTitle: "Intake Forms for Photographers",
  heroSubtitle: "Streamline client bookings, shoot requests, and qualification with AI-powered intake forms built for photography businesses.",
  heroDescription: "Whether you shoot weddings, portraits, events, or commercial work, HeyIntake helps you collect essential details upfront, qualify serious clients, and automate your booking process.",
  
  // Why AI Scoring section
  whyAIScoring: {
    title: "Why Photographers Need Smart Intake Forms",
    intro: "Your time behind the camera is valuable — but so is the time you spend qualifying leads and managing bookings. The right intake process can mean the difference between a packed calendar of dream clients and endless back-and-forth with tire-kickers.",
    paragraphs: [
      "Most photographers rely on basic contact forms or DMs to handle inquiries. This leads to hours spent answering the same questions: What's your budget? When is your event? What style are you looking for? Without structured intake, you waste time on clients who aren't a fit, miss important details, and lose high-value bookings to faster competitors.",
      "HeyIntake's AI-powered intake forms change the game. Capture every detail you need — event date, location, style preferences, budget range — in one professional form. Our AI instantly scores each inquiry based on fit with your services, budget alignment, and timeline, so you know exactly who to prioritize.",
      "For wedding photographers, portrait artists, event shooters, and commercial photographers alike, this means fewer wasted consultations, faster response times to serious inquiries, and more time doing what you love — creating stunning images."
    ]
  },

  painPoints: [
    {
      title: "Endless Email Back-and-Forth",
      description: "Spending hours exchanging messages just to gather basic information like event dates, locations, and budget ranges."
    },
    {
      title: "Unqualified Inquiries",
      description: "Booking consultations with clients whose budget doesn't match your pricing or who need services you don't offer."
    },
    {
      title: "Missing Critical Details",
      description: "Arriving at shoots without key information about the venue, timeline, or special requests because nothing was documented."
    },
    {
      title: "Slow Response Times",
      description: "Losing bookings to other photographers because you couldn't respond quickly enough to inquiries."
    },
    {
      title: "Inconsistent Client Experience",
      description: "Different clients receive different information requests, creating a disorganized and unprofessional impression."
    },
    {
      title: "No Way to Prioritize Leads",
      description: "High-budget wedding inquiries sit in the same inbox as quick headshot requests — all looking equally urgent."
    }
  ],

  exampleFields: [
    {
      category: "Contact & Event Basics",
      fields: [
        "Full name",
        "Email address",
        "Phone number",
        "Event/shoot date",
        "Event location or address"
      ]
    },
    {
      category: "Photography Details",
      fields: [
        "Type of shoot (wedding, portrait, event, commercial)",
        "Number of hours needed",
        "Number of people/subjects",
        "Indoor/outdoor preference",
        "Style inspiration (links or descriptions)"
      ]
    },
    {
      category: "Budget & Deliverables",
      fields: [
        "Budget range",
        "Desired deliverables (digital, prints, albums)",
        "Editing preferences",
        "Turnaround time expectations",
        "Additional services needed (second shooter, video)"
      ]
    },
    {
      category: "Special Requirements",
      fields: [
        "Shot list or must-have moments",
        "Venue restrictions or guidelines",
        "Timeline or schedule for the day",
        "Any accessibility needs",
        "How did you find me?"
      ]
    }
  ],

  // Example intake form section
  exampleIntakeForm: {
    title: "Example Photography Intake Form",
    description: "Here's what a high-converting photography booking form looks like:",
    fields: [
      { label: "What type of shoot are you booking?", type: "select", options: ["Wedding", "Portrait/Headshot", "Event", "Commercial/Product", "Family"] },
      { label: "Event/Shoot date", type: "text", placeholder: "MM/DD/YYYY" },
      { label: "Location", type: "text", placeholder: "City or venue name" },
      { label: "Budget range", type: "select", options: ["$500-$1,000", "$1,000-$2,500", "$2,500-$5,000", "$5,000+"] },
      { label: "Hours of coverage needed", type: "select", options: ["1-2 hours", "4 hours", "6 hours", "8+ hours (full day)"] },
      { label: "Style preference", type: "select", options: ["Light & airy", "Moody & dramatic", "Documentary/candid", "Classic/timeless", "Not sure"] },
      { label: "Anything else we should know?", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  // How It Works section
  howItWorks: {
    title: "How HeyIntake Works for Photographers",
    subtitle: "From inquiry to booked client in three simple steps",
    steps: [
      {
        title: "Client Completes Your Form",
        description: "Potential clients fill out your branded intake form with all the details you need — event date, budget, style preferences, and more."
      },
      {
        title: "AI Qualifies & Scores",
        description: "HeyIntake's AI analyzes each submission, scoring leads based on budget fit, timeline, and alignment with your services."
      },
      {
        title: "You Focus on Best Fits",
        description: "Prioritize high-value bookings, respond faster to serious inquiries, and spend less time on admin work."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Client Qualification for Photographers",
    description: "HeyIntake's AI analyzes each inquiry to help you identify your ideal clients instantly — so you can book more dream projects and waste less time on poor fits.",
    factors: [
      "Budget alignment with your pricing",
      "Event date availability",
      "Shoot type match with your expertise",
      "Completeness and seriousness of inquiry",
      "Location feasibility and travel requirements"
    ],
    exampleLead: {
      name: "Sarah & Michael",
      context: "Wedding Photography • June 2026 • $5,000 Budget",
      summary: "Full-day wedding coverage requested for June date at local venue. Budget matches premium package. Detailed style preferences provided. High-value booking opportunity.",
      score: 94
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Qualify Clients Before Consultations",
      description: "AI scoring identifies serious inquiries with matching budgets, so you know who's worth a call."
    },
    {
      icon: "clock",
      title: "Respond Faster to Dream Clients",
      description: "Get instant notifications for high-priority inquiries and beat competitors to the booking."
    },
    {
      icon: "file",
      title: "Collect Shot Lists & Inspiration",
      description: "Have clients upload Pinterest boards, reference photos, and must-have shot lists before you ever meet."
    },
    {
      icon: "users",
      title: "Professional Client Experience",
      description: "Every client receives the same polished intake process, making your business look more established."
    },
    {
      icon: "chart",
      title: "Track Your Lead Pipeline",
      description: "See all your inquiries in one place with status tracking from initial contact to booked client."
    },
    {
      icon: "zap",
      title: "Automate Follow-Ups",
      description: "Set up automatic emails for new inquiries, booking confirmations, and pre-shoot reminders."
    }
  ],

  // Cross-linking to related industries
  relatedIndustries: [
    { name: "Intake Forms for Agencies", path: "/intake-form-for-agencies" },
    { name: "Intake Forms for Contractors", path: "/intake-form-for-contractors" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" },
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" }
  ],

  ctaText: "Ready to Book More Dream Clients?"
};
